import React, {useEffect, useState} from "react";
import IconButton from "../../base/button/icon_button";
import {ReactComponent as ChartIcon} from "../../../../assets/images/products/chart.svg";
import {ReactComponent as HistoryIcon} from "../../../../assets/images/products/history.svg";
import ConfigurableInfoBlockFeedback from "./feedback";
import ConfigurableInfoBlockInput from "./input";
import useIsMounted from "../../../hooks/use-is-mounted";
import classnames from "classnames";
import {Fade} from "@mui/material";
import FormulaInputHelpTooltip from "./input/formula-input/help-tooltip";

export const ConfigurableInfoBlockInputTypes = {
    input: "input",
    select: "select",
    ckEditor: "ckEditor",
    category: "category",
    tagsAutoComplete: "tagsAutoComplete",
    formsAutoComplete: "formsAutoComplete",
    autocomplete: "autocomplete",
    toggle: 'toggle',
    formula: 'formula',
}

export const ConfigurableInfoBlockFeedbackIconPositions = {
    withTitle: 'withTitle',
    withInput: 'withInput',
}

const initFeedback = {show: false, success: null, message: null};

const ConfigurableInfoBlock = ({
                                   children,
                                   title = '',
                                   showChartIcon = false,
                                   customIcons,
                                   additionalInfo,
                                   showCustomIcons = false,
                                   onChartIconClicked,
                                   showHistoryIcon = false,
                                   onHistoryIconClicked,
                                   inputType = ConfigurableInfoBlockInputTypes.input,
                                   inputProps = {},
                                   feedbackIconPosition = ConfigurableInfoBlockFeedbackIconPositions.withInput,
                                   onValueChanged,
                                   className,
                                   hideInput = false,
                                   titleFullWidth = false,
                                   hideFeedback = false,
                               }) => {
    const [feedback, setFeedback] = useState(initFeedback);
    const [loading, setLoading] = useState(false);
    const isMounted = useIsMounted();

    /**
     * With each change in the feedback visibility state:
     * - if the feedback is visible, removes the feedback after 10 seconds.
     */
    useEffect(() => {
        if (!feedback.show) return;
        const timer = setTimeout(() => {
            setFeedback(initFeedback);
        }, feedback.success ? 5000 : 10000)
        return () => {
            clearTimeout(timer)
        }
    }, [feedback.show])

    /**
     * Invokes and awaits for the response of the onValueChanged callback to get the result of the update
     *
     * * sets the feedback based on the response
     * @param {string} value
     * @return {Promise<void>}
     */
    const onInputValueChanged = async (value) => {
        if (!onValueChanged) {
            return setFeedback(initFeedback);
        }
        setLoading(true);
        const response = await onValueChanged(value);
        if (!isMounted()) return;
        setLoading(false);
        if (!response) {
            return setFeedback(initFeedback);
        }
        setFeedback({
            show: true,
            success: !!response?.resultFlag,
            message: response?.message,
        })
    }

    return (
        <>
            <div className={classnames('configurable-info-block', className)}>
                <div className={'d-flex flex-row justify-content-between w-100'}>
                    <div className={classnames('title-section', {
                        'ml-3': inputType === ConfigurableInfoBlockInputTypes.category,
                    })}>
                        <p className={classnames('title', {'full-width': titleFullWidth})}>
                            {title ?? ''}
                        </p>
                        <div className={classnames('d-flex align-items-center')}>
                            {
                                showChartIcon &&
                                <IconButton
                                    color={'primary'}
                                    onPressed={onChartIconClicked}
                                    tabIndex={-1}
                                >
                                    <ChartIcon/>
                                </IconButton>
                            }
                            {
                                showHistoryIcon &&
                                <IconButton
                                    color={'primary'}
                                    onPressed={onHistoryIconClicked}
                                >
                                    <HistoryIcon/>
                                </IconButton>
                            }
                            {showCustomIcons && customIcons}
                            {
                                inputType === ConfigurableInfoBlockInputTypes.formula && <FormulaInputHelpTooltip inputProps={inputProps}/>
                            }
                        </div>
                        {
                            !hideFeedback &&
                            feedbackIconPosition === ConfigurableInfoBlockFeedbackIconPositions.withTitle &&
                            <ConfigurableInfoBlockFeedback feedback={feedback} loading={loading}/>
                        }
                    </div>
                    {additionalInfo}
                </div>
                {children}
                {
                    !hideInput &&
                    <div className={'input-section'}>
                        <ConfigurableInfoBlockInput
                            loading={loading}
                            inputType={inputType}
                            feedback={feedback}
                            onChanged={onInputValueChanged}
                            {...inputProps}
                        />
                        {
                            !hideFeedback &&
                            feedbackIconPosition === ConfigurableInfoBlockFeedbackIconPositions.withInput &&
                            <ConfigurableInfoBlockFeedback feedback={feedback} loading={loading}/>
                        }
                    </div>
                }
                {
                    !hideFeedback &&
                    <Fade
                        in={!!(feedback.show && feedback.success === false && !!feedback.message)}
                        mountOnEnter
                        unmountOnExit
                    >
                        <p className={'feedback-message'}>
                            {feedback.message}
                        </p>
                    </Fade>
                }
            </div>
        </>
    )
}

export default ConfigurableInfoBlock;
